import { useState, useEffect } from "react";
import axios from "axios";
import {
  Loader2, ChevronLeft, ChevronRight, Umbrella, Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, eachDayOfInterval, isSameMonth, isSameDay } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const TYPE_COLOURS = {
  annual: "bg-blue-200 text-blue-800",
  sick: "bg-red-200 text-red-800",
  study: "bg-purple-200 text-purple-800",
  family: "bg-amber-200 text-amber-800",
  responsibility: "bg-orange-200 text-orange-800",
  long_service: "bg-teal-200 text-teal-800",
  unpaid: "bg-slate-200 text-slate-600",
};

export default function LMSCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [department, setDepartment] = useState("");

  useEffect(() => { fetchCalendar(); }, [currentDate]);

  const fetchCalendar = async () => {
    setLoading(true);
    try {
      const range = getDateRange();
      const params = { start_date: range.start, end_date: range.end };
      if (department) params.department = department;
      const res = await axios.get(`${API}/lms/calendar`, {
        params, headers: getHeaders(), withCredentials: true,
      });
      setEntries(res.data.entries || []);
    } catch {}
    setLoading(false);
  };

  const getDateRange = () => {
    const start = startOfWeek(startOfMonth(currentDate));
    const end = endOfWeek(endOfMonth(currentDate));
    return { start: start.toISOString(), end: end.toISOString() };
  };

  const getEntriesForDay = (day) =>
    entries.filter((e) => e.date === format(day, "yyyy-MM-dd"));

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calStart = startOfWeek(monthStart);
  const calEnd = endOfWeek(monthEnd);
  const days = eachDayOfInterval({ start: calStart, end: calEnd });

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-slate-800">Leave Calendar</h2>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => setCurrentDate(subMonths(currentDate, 1))}
              className="h-7 w-7 p-0 rounded-none">
              <ChevronLeft className="w-3 h-3" />
            </Button>
            <span className="text-sm font-semibold text-slate-700 min-w-[140px] text-center">
              {format(currentDate, "MMMM yyyy")}
            </span>
            <Button variant="outline" size="sm" onClick={() => setCurrentDate(addMonths(currentDate, 1))}
              className="h-7 w-7 p-0 rounded-none">
              <ChevronRight className="w-3 h-3" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setCurrentDate(new Date())}
              className="h-7 text-xs px-2 rounded-none">
              Today
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input value={department} onChange={(e) => setDepartment(e.target.value)}
            placeholder="Filter department..."
            className="h-7 text-[10px] rounded-none w-44" />
          <Button variant="outline" size="sm" onClick={fetchCalendar}
            className="h-7 text-xs rounded-none">
            Refresh
          </Button>
        </div>
      </div>

      {/* Colour Legend */}
      <div className="flex items-center gap-3 mb-3 text-[10px]">
        <span className="text-slate-500">Legend:</span>
        {Object.entries(TYPE_COLOURS).map(([code, cls]) => (
          <span key={code} className={`px-1.5 py-0.5 rounded ${cls}`}>{code}</span>
        ))}
      </div>

      {/* Calendar Grid */}
      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-emerald-500 mx-auto py-8" />
      ) : (
        <div className="flex-1 bg-white border border-slate-200">
          <div className="grid grid-cols-7 border-b border-slate-200">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className="px-2 py-1.5 text-[10px] font-medium text-slate-400 text-center uppercase">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 flex-1">
            {days.map((day) => {
              const dayEntries = getEntriesForDay(day);
              const isToday = isSameDay(day, new Date());
              const isCurrentMonth = isSameMonth(day, currentDate);

              return (
                <div key={day.toISOString()}
                  className={`min-h-[90px] border-b border-r border-slate-100 p-1 ${
                    !isCurrentMonth ? "bg-app/50" : ""
                  }`}
                >
                  <span className={`inline-flex items-center justify-center w-5 h-5 text-[10px] rounded-full ${
                    isToday ? "bg-emerald-600 text-white font-semibold" : isCurrentMonth ? "text-slate-700" : "text-slate-300"
                  }`}>
                    {format(day, "d")}
                  </span>
                  <div className="mt-0.5 space-y-0.5">
                    {dayEntries.slice(0, 4).map((entry, i) => (
                      <div key={i}
                        className={`text-[8px] px-1 py-0.5 rounded truncate border ${
                          TYPE_COLOURS[entry.leave_type_code] || "bg-slate-100 text-slate-600"
                        }`}
                        title={`${entry.employee_name} - ${entry.leave_type_name}`}
                      >
                        {entry.employee_name?.split(" ").slice(-1)[0]}: {entry.leave_type_code}
                      </div>
                    ))}
                    {dayEntries.length > 4 && (
                      <div className="text-[8px] text-slate-400 px-1">+{dayEntries.length - 4} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
